import React, { useState, useEffect, useContext } from "react";
import request from "../../utilities/requests";
import { Box, Dialog, DialogTitle, DialogContent, Slide } from "@mui/material";
import {
  Typography,
  CircularProgress,
  AppBar,
  IconButton,
  Toolbar,
  TextField,
  Button,
  ButtonGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { EventContext } from "../../contexts/EventContext";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Messaging = () => {
  const { messageCount, setMessageCount } = useContext(EventContext);
  const [activeFilter, setActiveFilter] = useState("new");
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [thread, setThread] = useState([]);
  const [threadPhone, setThreadPhone] = useState(null);
  const [threadName, setThreadName] = useState("");
  const [outboundText, setOutboundText] = useState("");
  const threadEndRef = React.useRef(null);
  const threadContainerRef = React.useRef(null);

  const [threadPage, setThreadPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Summaries pagination state
  const [summaryPage, setSummaryPage] = useState(0);
  const [summaryHasMore, setSummaryHasMore] = useState(true);
  const [loadingMoreSummaries, setLoadingMoreSummaries] = useState(false);
  const summariesContainerRef = React.useRef(null);

  useEffect(() => {
    loadSummaries(true);
  }, []);

  useEffect(() => {
    // Only refresh if the Messaging page is currently open
    if (threadPhone) {
      // If viewing a conversation thread, refresh that thread
      loadThread(threadPhone, 0, false);
    } else {
      // If on the summary list, refresh summaries based on filter
      loadSummaries(activeFilter === "new");
    }
  }, [messageCount]);

  useEffect(() => {
    if (threadPage === 0 && threadEndRef.current) {
      threadEndRef.current.scrollIntoView({ behavior: "auto" });
    }
  }, [thread, threadPage]);

  const formatTimeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const loadSummaries = async (unread, page = 0, append = false) => {
    if (append) {
      setLoadingMoreSummaries(true);
    } else {
      setLoading(true);
    }

    let url = `messaging?page=${page}`;
    if (unread) {
      url += `&unread=true`;
    }

    const result = await request(url);

    if (result.success) {
      if (append) {
        setMessages((prev) => [...prev, ...result.data]);
        setSummaryPage(page);
        setSummaryHasMore(result.data.length === 25);
      } else {
        setMessages(result.data);
        setSummaryPage(0);
        setSummaryHasMore(result.data.length === 25);
      }
    }

    setLoading(false);
    setLoadingMoreSummaries(false);
  };

  const loadThread = async (phone, page = 0, append = false) => {
    if (!phone) return;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    const result = await request(`messaging?phone=${phone}&page=${page}`);

    if (result.success) {
      if (append) {
        const container = threadContainerRef.current;
        const previousHeight = container ? container.scrollHeight : 0;

        setThread((prev) => [...result.data, ...prev]);
        setThreadPage(page);
        setHasMore(result.pagination?.hasMore);

        setTimeout(() => {
          if (container) {
            const newHeight = container.scrollHeight;
            container.scrollTop = newHeight - previousHeight;
          }
        }, 0);
      } else {
        setThread(result.data);
        setThreadPage(0);
        setHasMore(result.pagination?.hasMore);
      }
    }

    setLoading(false);
    setLoadingMore(false);
  };

  const sendOutbound = async () => {
    if (!outboundText.trim() || !threadPhone) return;
    const userId = thread.length > 0 ? thread[0].user : null;
    const body = { phone: threadPhone, body: outboundText, userId };
    const result = await request("messaging", "post", body);

    if (result.success) {
      setOutboundText("");
      loadThread(threadPhone);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const result = await request("messaging/unread");
      if (result.success) {
        setMessageCount(result.count);
      }
    } catch (error) {
      console.log("Error fetching unread count", error);
    }
  };

  return (
    <>
      <Typography
        variant="h5"
        sx={{ p: 2, fontWeight: 600, textAlign: "center" }}
      >
        Messaging
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <ButtonGroup variant="outlined">
          <Button
            size="small"
            variant={activeFilter === "new" ? "contained" : "outlined"}
            onClick={() => {
              setActiveFilter("new");
              loadSummaries(true, 0, false);
            }}
          >
            New
          </Button>
          <Button
            size="small"
            variant={activeFilter === "all" ? "contained" : "outlined"}
            onClick={() => {
              setActiveFilter("all");
              loadSummaries(false, 0, false);
            }}
          >
            All
          </Button>
        </ButtonGroup>
      </Box>

<Box
  ref={summariesContainerRef}
  onScroll={(e) => {
    const container = e.currentTarget;
    if (
      container.scrollTop + container.clientHeight >= container.scrollHeight - 10 &&
      summaryHasMore &&
      !loadingMoreSummaries &&
      !loading
    ) {
      loadSummaries(activeFilter === "new", summaryPage + 1, true);
    }
  }}
  sx={{
    p: 2,
    textAlign: "center",
    height: "calc(100vh - 200px)",
    overflowY: "auto",
  }}
>
  {loading && <CircularProgress />}

  {!loading && messages.length === 0 && (
    <Typography sx={{ mt: 2 }}>
      All caught up! No messages to display.
    </Typography>
  )}

  {!loading &&
    messages.map((m) => {
      return (
        <Box
          key={m.phone}
          onClick={() => {
            loadThread(m.phone, 0, false);
            setThreadPhone(m.phone);
            setThreadName(`${m.firstName} ${m.lastName}`);
          }}
          sx={{
            p: 2,
            mb: 2,
            border:
              m.read === false ? "2px solid #000" : "2px solid #ccc",
            borderRadius: "6px",
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: m.read === false ? 700 : 400,
          }}
        >
          <Box>
            <strong>
              {m.firstName} {m.lastName}
            </strong>
            <div>{m.phone}</div>
          </Box>
          <Box>{m.lastMessage}</Box>
          <Box sx={{ opacity: 0.7 }}>{formatTimeAgo(m.createdAt)}</Box>
        </Box>
      );
    })}
  {loadingMoreSummaries && (
    <Box sx={{ textAlign: "center", py: 2 }}>
      <CircularProgress size={24} />
    </Box>
  )}
</Box>

      <Dialog
        fullScreen
        open={!!threadPhone}
        onClose={() => {
          setThreadPhone(null);
          setThreadName("");
          setThread([]);
        }}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => {
                setThreadPhone(null);
                setThreadName("");
                setThread([]);
                loadSummaries();
                fetchUnreadCount();
              }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography
              sx={{ ml: 2, flex: 1, fontWeight: 600 }}
              variant="h6"
              component="div"
            >
              {threadName || threadPhone}
            </Typography>
          </Toolbar>
        </AppBar>

        <DialogContent
          ref={threadContainerRef}
          onScroll={(e) => {
            const container = e.currentTarget;
            if (
              container.scrollTop === 0 &&
              hasMore &&
              !loadingMore &&
              !loading
            ) {
              loadThread(threadPhone, threadPage + 1, true);
            }
          }}
          sx={{
            p: 2,
            backgroundColor: "#f2f2f7",
            height: "100%",
            overflowY: "auto",
          }}
        >
          {thread.map((msg, i) => {
            const isOutbound = msg.direction === "outbound";
            return (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  justifyContent: isOutbound ? "flex-end" : "flex-start",
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    maxWidth: "75%",
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: isOutbound ? "#007aff" : "#e5e5ea",
                    color: isOutbound ? "white" : "black",
                    whiteSpace: "pre-wrap",
                    fontSize: "0.95rem",
                  }}
                >
                  {msg.body}
                  <Box
                    sx={{
                      fontSize: "0.7rem",
                      opacity: 0.6,
                      mt: 0.5,
                      textAlign: "right",
                    }}
                  >
                    {new Date(msg.createdAt).toLocaleString()}
                  </Box>
                </Box>
              </Box>
            );
          })}
          {loadingMore && (
            <Box sx={{ textAlign: "center", py: 1 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          <div ref={threadEndRef} />
        </DialogContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 2,
            borderTop: "1px solid #ccc",
            background: "white",
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Type a message..."
            value={outboundText}
            onChange={(e) => setOutboundText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && outboundText.trim()) {
                sendOutbound();
              }
            }}
            sx={{ mr: 1 }}
          />
          <Button
            variant="contained"
            onClick={sendOutbound}
            disabled={!outboundText.trim()}
          >
            Send
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default Messaging;
